import axios from "axios";
import Country from "models/Country";
import Transport from "models/Transport";
import StayType from "models/StayType";
import Budget from "models/Budget";
import Duration from "models/Duration";
import { GenericService, Service } from "./genericService";

// Create the request object
interface SearchDestinationRequest {
  from: Date;
  to: Date;
  transports?: Transport[];
  durations?: Duration[];
  stayTypes: StayType[];
  budgets?: Budget[];
  destination?: Country[];
}

export interface TravelIntentMatchedKeyword {
  keyword: string;
  ambiance: string;
  countryId: number;
  countryName: string;
  weight: number;
}

export interface TravelIntentCountryScore {
  countryId: number;
  countryName: string;
  isoCode?: string;
  flagUri?: string;
  ambiance: string;
  score: number;
}

export interface TravelIntentResult {
  country: {
    id: number;
    name: string;
    isoCode?: string;
    flagUri?: string;
  };
  ambiance: string;
  score: number;
  matchedKeywords: TravelIntentMatchedKeyword[];
  alternatives: TravelIntentCountryScore[];
}

class CountryService
  extends GenericService<Country>
  implements Service<Country> {
  constructor() {
    super("api/Country");
  }

  getAllShortDisplay = async (): Promise<Country[]> => {
    const result = await axios.get<Country[]>(`${this.baseUrl}/shortDisplay`);
    return result.data;
  };

  getBriefDisplay = async (ids: number[]): Promise<Country> => {
    const result = await axios.post<Country>(
      `${this.baseUrl}/briefDisplay`,
      JSON.stringify(ids),
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return result.data;
  };

  getFullBriefDisplay = async (id: number): Promise<Country> => {
    const result = await axios.get<Country>(
      `${this.baseUrl}/fullBriefDisplay/${id}`,
    );
    return result.data;
  };

  searchWithCriteria = async (
    request: SearchDestinationRequest, page: number
  ): Promise<Country[]> => {
    const result = await axios.post<Country[]>(
      `${this.baseUrl}/searchWithCriteria?page=${page}`,
      { ...request },
    );
    return result.data;
  };

  getSuggestions = async (): Promise<Country[]> => {
    const result = await axios.get<Country[]>(
      `${this.baseUrl}/suggestions`
    );
    return result.data;
  }

  searchTravelIntent = async (text: string): Promise<TravelIntentResult | null> => {
    const result = await axios.post<TravelIntentResult>(
      `${this.baseUrl}/travelIntent`,
      { text },
    );

    return result.status === 204 ? null : result.data;
  }
}

export default new CountryService();
